package com.gavino.nutritrack

import kotlin.math.roundToInt

expect suspend fun platformHttpPut(url: String, jsonBody: String): Boolean
expect suspend fun platformHttpGet(url: String): String?

expect fun platformShareText(title: String, text: String)

object MealShareFormatter {

    fun formatSingleMeal(
        dayLabel: String,
        section: MealSectionType,
        items: List<FoodItem>
    ): String {
        val sb = StringBuilder()
        sb.append("${section.icon} *${section.title}* ($dayLabel)\n")
        sb.append("────────────────────\n")
        items.forEach { item ->
            val grams = if (item.isConsumed) item.consumedGrams else item.currentGrams
            sb.append("• *${item.name}*: ${grams}g\n")
        }
        val totCal = items.sumOf { if (it.isConsumed) it.eatenCal else it.currentCal }
        sb.append("────────────────────\n")
        sb.append("🔥 Totale pasto: *$totCal kcal*")
        return sb.toString()
    }

    fun formatFullDay(
        dayLabel: String,
        mealsMap: Map<MealSectionType, List<FoodItem>>,
        waterGlasses: Int
    ): String {
        val sb = StringBuilder()
        sb.append("📋 *MENU DEL GIORNO ($dayLabel)*\n")
        sb.append("════════════════════\n\n")

        MealSectionType.entries.forEach { section ->
            val items = mealsMap[section] ?: emptyList()
            if (items.isNotEmpty()) {
                sb.append("${section.icon} *${section.title}*\n")
                items.forEach { item ->
                    val grams = if (item.isConsumed) item.consumedGrams else item.currentGrams
                    sb.append("  • ${item.name}: *${grams}g*\n")
                }
                sb.append("\n")
            }
        }

        val allItems = mealsMap.values.flatten()
        val totCal = allItems.sumOf { if (it.isConsumed) it.eatenCal else it.currentCal }
        sb.append("════════════════════\n")
        sb.append("🔥 *Totale Giornata:* $totCal kcal\n")
        if (waterGlasses > 0) {
            sb.append("💧 *Acqua:* $waterGlasses bicchieri (${waterGlasses * 250} ml)")
        }
        return sb.toString()
    }
}

data class CloudAppState(
    val selectedDayNum: Int,
    val scaleFactor: Double,
    val targetCalInput: String,
    val waterGlasses: Int,
    val userProfile: UserProfileData,
    val weightLog: List<WeightEntry>,
    val allDaysMeals: Map<Int, Map<MealSectionType, List<FoodItem>>>,
    val assignedDaysMap: Map<Int, String> = emptyMap(),
    val weekKey: String = ""
)

object FirebaseSyncManager {

    var firebaseDbUrl: String = "https://nutritrack-36ff6-default-rtdb.europe-west1.firebasedatabase.app"
    private const val FALLBACK_US_URL = "https://nutritrack-36ff6-default-rtdb.firebaseio.com"
    var userSyncKey: String = "gavino_coletti"

    private fun buildEndpoint(baseUrl: String): String {
        val cleanBase = baseUrl.trim().removeSuffix("/")
        return "$cleanBase/users/$userSyncKey/appState.json"
    }

    suspend fun pushToFirebase(state: CloudAppState): Boolean {
        val serialized = serializeState(state)
        val jsonBody = "\"${serialized.replace("\"", "\\\"")}\""

        val okPrimary = platformHttpPut(buildEndpoint(firebaseDbUrl), jsonBody)
        if (okPrimary) return true

        val okFallback = platformHttpPut(buildEndpoint(FALLBACK_US_URL), jsonBody)
        if (okFallback) {
            firebaseDbUrl = FALLBACK_US_URL
            return true
        }
        return false
    }

    suspend fun pullFromFirebase(): CloudAppState? {
        var raw = platformHttpGet(buildEndpoint(firebaseDbUrl))
        if (raw.isNullOrBlank() || raw == "null") {
            raw = platformHttpGet(buildEndpoint(FALLBACK_US_URL))
            if (!raw.isNullOrBlank() && raw != "null") {
                firebaseDbUrl = FALLBACK_US_URL
            }
        }
        if (raw.isNullOrBlank() || raw == "null") return null
        return deserializeState(raw)
    }

    fun serializeState(state: CloudAppState): String {
        val sb = StringBuilder()
        sb.append("V4|")
        sb.append("${state.selectedDayNum};${state.scaleFactor};${state.targetCalInput};${state.waterGlasses}|")

        val p = state.userProfile
        sb.append("${p.gender.name};${p.age};${p.heightCm};${p.currentWeightKg};${p.targetWeightKg};${p.waistCm};${p.activityLevel.name};${p.deficitGoal.name}|")

        val wStr = state.weightLog.joinToString("~") { w ->
            "${clean(w.id)}^${clean(w.dateLabel)}^${w.weightKg}^${w.waistCm}^${clean(w.note)}"
        }
        sb.append("$wStr|")

        val mealsList = mutableListOf<String>()
        state.allDaysMeals.forEach { (dayNum, map) ->
            map.forEach { (section, items) ->
                items.forEach { it ->
                    mealsList.add(
                        listOf(
                            dayNum.toString(),
                            section.name,
                            clean(it.id),
                            clean(it.name),
                            it.slotType.name,
                            it.baseGrams,
                            it.currentGrams,
                            it.baseCal,
                            it.baseCarb,
                            it.baseProt,
                            it.baseFat,
                            clean(it.compNote),
                            it.swapGroupId ?: "NONE",
                            it.recipeId ?: "NONE",
                            it.isConsumed,
                            it.consumedGrams,
                            it.isExtraItem,
                            clean(it.originalPrescribedName),
                            it.originalPrescribedGrams,
                            it.originalPrescribedCal,
                            clean(it.originalCompNote)
                        ).joinToString("^")
                    )
                }
            }
        }
        sb.append(mealsList.joinToString("~"))
        sb.append("|${clean(state.weekKey)}|")
        val assignedStr = state.assignedDaysMap.entries.joinToString("~") { "${it.key}^${clean(it.value)}" }
        sb.append(assignedStr)
        return sb.toString()
    }

    fun deserializeState(payload: String): CloudAppState? {
        return try {
            val cleanPayload = payload.trim()
                .removeSurrounding("\"")
                .replace("\\\"", "\"")
            val parts = cleanPayload.split("|")
            if (parts.size < 5) return null
            val version = parts[0]
            if (version != "V1" && version != "V2" && version != "V3" && version != "V4") return null

            val head = parts[1].split(";")
            val selectedDayNum = head[0].toInt()
            val scaleFactor = head[1].toDouble()
            val targetCalInput = head[2]
            val waterGlasses = head[3].toInt()

            val prof = parts[2].split(";")
            val userProfile = UserProfileData(
                gender = Gender.valueOf(prof[0]),
                age = prof[1].toInt(),
                heightCm = prof[2].toInt(),
                currentWeightKg = prof[3].toDouble(),
                targetWeightKg = prof[4].toDouble(),
                waistCm = prof[5].toInt(),
                activityLevel = ActivityLevel.valueOf(prof[6]),
                deficitGoal = DeficitGoal.valueOf(prof[7])
            )

            val weightLog = if (parts[3].isBlank()) emptyList() else {
                parts[3].split("~").mapNotNull { row ->
                    val cols = row.split("^")
                    if (cols.size >= 5) {
                        WeightEntry(cols[0], cols[1], cols[2].toDouble(), cols[3].toInt(), cols[4])
                    } else null
                }
            }

            val allDaysMeals = mutableMapOf<Int, MutableMap<MealSectionType, MutableList<FoodItem>>>()
            if (version == "V3" || version == "V4") {
                if (parts[4].isNotBlank()) {
                    parts[4].split("~").forEach { row ->
                        val c = row.split("^")
                        if (c.size >= 17) {
                            val dayNum = c[0].toInt()
                            val sec = MealSectionType.valueOf(c[1])
                            val origName = if (c.size >= 18) c[17] else c[3]
                            val origGrams = if (c.size >= 19) c[18].toInt() else c[5].toInt()
                            val origCal = if (c.size >= 20) c[19].toInt() else c[7].toInt()
                            val origComp = if (c.size >= 21) c[20] else c[11]

                            // Correzione automatica se un alimento salvato in precedenza aveva baseCal/baseGrams disallineati
                            val rawBaseG = c[5].toInt().coerceAtLeast(1)
                            val rawCurrG = c[6].toInt().coerceAtLeast(1)
                            val rawBaseCal = c[7].toInt()
                            val impliedCurrCal = (rawBaseCal * (rawCurrG.toDouble() / rawBaseG.toDouble())).roundToInt()
                            val fixedBaseG = if (impliedCurrCal > origCal * 2 && rawBaseCal <= origCal * 1.3) rawCurrG else rawBaseG

                            val item = FoodItem(
                                id = c[2],
                                name = c[3],
                                slotType = MealSlotType.valueOf(c[4]),
                                baseGrams = fixedBaseG,
                                currentGrams = rawCurrG,
                                baseCal = rawBaseCal,
                                baseCarb = c[8].toDouble(),
                                baseProt = c[9].toDouble(),
                                baseFat = c[10].toDouble(),
                                compNote = c[11],
                                swapGroupId = if (c[12] == "NONE") null else c[12],
                                recipeId = if (c[13] == "NONE") null else c[13],
                                originalPrescribedName = origName,
                                originalPrescribedGrams = origGrams,
                                originalPrescribedCal = origCal,
                                originalCompNote = origComp,
                                isConsumed = c[14].toBoolean(),
                                consumedGrams = c[15].toInt(),
                                isExtraItem = c[16].toBoolean()
                            )
                            if (allDaysMeals[dayNum] == null) {
                                allDaysMeals[dayNum] = mutableMapOf()
                                MealSectionType.entries.forEach { allDaysMeals[dayNum]!![it] = mutableListOf() }
                            }
                            allDaysMeals[dayNum]!![sec]?.add(item)
                        }
                    }
                }
            }

            val weekKey = if (parts.size >= 6) parts[5] else ""
            val assignedDaysMap = if (parts.size >= 7 && parts[6].isNotBlank()) {
                parts[6].split("~").mapNotNull {
                    val pair = it.split("^")
                    if (pair.size >= 2) pair[0].toIntOrNull()?.let { day -> day to pair[1].trim() } else null
                }.toMap()
            } else emptyMap()

            CloudAppState(
                selectedDayNum = selectedDayNum,
                scaleFactor = scaleFactor,
                targetCalInput = targetCalInput,
                waterGlasses = waterGlasses,
                userProfile = userProfile,
                weightLog = weightLog,
                allDaysMeals = allDaysMeals,
                assignedDaysMap = assignedDaysMap,
                weekKey = weekKey
            )
        } catch (e: Exception) {
            null
        }
    }

    private fun clean(s: String): String =
        s.replace("|", " ").replace("~", " ").replace("^", " ").replace(";", " ").replace("\"", "'")
}